import { Button } from "@/atoms-react/button/Button.react.tsx";
import Image from "@/atoms-react/image/Image.react.tsx";
import { Base, SectionTitleSmall } from "@/atoms-react/text/Text.react.tsx";
import { useKYC } from "@/features/kyc/hooks/use-kyc.ts";
import type { CreateKYCDocument } from "@/features/kyc/types.ts";
import type { ModalIbanAccountStep, QuizStepProps } from "@/features/user/quiz/types.ts";
import { QuestionScenario } from "@/molecules/modal-iban-account/quiz/question-scenario.react.tsx";
import { Themes } from "@/types/theme.ts";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export const VideoVerificationStep = ({
  setStep,
  stepSettings,
  setPrevStep,
  user,
  quizResponses,
  custom_scenario = false
}: QuizStepProps) => {
  const { t } = useTranslation(["pass"]);
  const [showQuestionVideo, setShowQuestionVideo] = useState<boolean>(false);
  const toggleVideoModal = () => {
    // launch video
    // if video successfully
    setShowQuestionVideo(!showQuestionVideo);
  };
  const { updateQuizVideoQuestionMutation } = useKYC();
  const { mutateAsync: editSession } = updateQuizVideoQuestionMutation();
  const onQuestionSessionEnd = async (data: CreateKYCDocument) => {
    // update the session with the informations from the iframe message
    try {
      await editSession(data);
      setStep(stepSettings.next as ModalIbanAccountStep);
    } catch (_) {
      toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.video_verification.error"));
      setShowQuestionVideo(false);
    }
  };

  return (
    <>
      <SectionTitleSmall className={"mb-2"}>
        {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.video_verification.section_title")}
      </SectionTitleSmall>
      {!showQuestionVideo && (
        <>
          <Base className="mt-4 mb-4">
            {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.video_verification.description")}
          </Base>
          <div
            className="z-50 mb-4 h-full cursor-pointer rounded-sm border-2 border-slate-50 px-4 py-4 hover:bg-slate-50 md:px-6 md:py-6 dark:hover:bg-slate-800"
            onClick={toggleVideoModal}
          >
            <div className="flex h-full flex-col items-center items-center justify-center">
              <div className="h-10 w-10">
                <Image src="/images/olkypass/play_video.svg" alt="Play icon" />
              </div>
              <Base className="mt-2 text-center text-sky-500">
                {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.video_verification.launch")}
              </Base>
            </div>
          </div>
          <div className="mt-auto mb-4 flex flex-col items-center">
            <div className="flex w-full flex-col-reverse gap-y-2 md:flex-row md:gap-x-2">
              <Button
                theme={Themes.white}
                onClick={() => setPrevStep(stepSettings.previous as ModalIbanAccountStep)}
              >
                {t("olkypass.general.go_back")}
              </Button>

              <Button dataCy="video-step-continue" onClick={() => toggleVideoModal()}>
                {t("olkypass.general.continue")}
              </Button>
            </div>
          </div>
        </>
      )}
      {showQuestionVideo && (
        <div className="max-h-[550px] overflow-y-auto">
          <QuestionScenario
            custom_scenario={custom_scenario}
            user={user}
            documentType={quizResponses?.documentType?.type_en}
            onValidated={onQuestionSessionEnd}
          ></QuestionScenario>
        </div>
      )}
    </>
  );
};
